import React, { createContext, useContext, useMemo } from "react";

// ============================================================================
// Collection Item Models
// ============================================================================

/**
 * A program booking in the reservation.
 * These values come from the reservation's ProgramBookings array.
 */
export interface ProgramBookingItem {
  ClosureNote?: string;
  /** Display name of the program (e.g., "Weight Loss Program") */
  Name: string;
  /** Number of program days */
  Days: number;
  /** Formatted start date (e.g., "March 1, 2025") */
  StartDate: string;
  /** Formatted end date (e.g., "March 14, 2025") */
  EndDate: string;
  /** Formatted price (e.g., "฿35,000") */
  Price: string;
  /** Quantity (for grouped programs) */
  Quantity: number;
}

/**
 * A room booking in the reservation.
 * These values come from the reservation's RoomBookings array.
 */
export interface RoomBookingItem {
  /** Display name of the room type (e.g., "Superior Room") */
  RoomType: string;
  /** Number of nights */
  Nights: number;
  /** Formatted check-in date (e.g., "March 1, 2025") */
  CheckInDate: string;
  /** Formatted check-out date (e.g., "March 15, 2025") */
  CheckOutDate: string;
  /** Formatted price (e.g., "฿21,000") */
  Price: string;
  /** Quantity (for grouped rooms) */
  Quantity: number;
}

/**
 * A POS (point of sale) item in the reservation.
 * These values come from the reservation's PosItems array.
 */
export interface PosItemData {
  /** Description of the item (e.g., "Airport Transfer") */
  Description: string;
  /** Quantity */
  Quantity: number;
  /** Formatted price (e.g., "฿2,000") */
  Price: string;
}

// ============================================================================
// Collection Contexts - One for each collection type
// ============================================================================

/**
 * Context value for a program repeater iteration.
 */
export interface ProgramIterationContext {
  /** The current program item */
  program: ProgramBookingItem;
  /** Zero-based index */
  index: number;
  /** One-based ordinal */
  ordinal: number;
  /** Total count of programs */
  count: number;
}

/**
 * Context value for a room repeater iteration.
 */
export interface RoomIterationContext {
  /** The current room item */
  room: RoomBookingItem;
  /** Zero-based index */
  index: number;
  /** One-based ordinal */
  ordinal: number;
  /** Total count of rooms */
  count: number;
}

/**
 * Context value for a POS item repeater iteration.
 */
export interface PosIterationContext {
  /** The current POS item */
  posItem: PosItemData;
  /** Zero-based index */
  index: number;
  /** One-based ordinal */
  ordinal: number;
  /** Total count of POS items */
  count: number;
}

// Create contexts
const ProgramIterationCtx = createContext<ProgramIterationContext | null>(null);
const RoomIterationCtx = createContext<RoomIterationContext | null>(null);
const PosIterationCtx = createContext<PosIterationContext | null>(null);

// ============================================================================
// Providers
// ============================================================================

export function ProgramIterationProvider(props: {
  program: ProgramBookingItem;
  index: number;
  count: number;
  children?: React.ReactNode;
}) {
  const value = useMemo<ProgramIterationContext>(
    () => ({
      program: props.program,
      index: props.index,
      ordinal: props.index + 1,
      count: props.count,
    }),
    [props.program, props.index, props.count]
  );

  return (
    <ProgramIterationCtx.Provider value={value}>
      {props.children}
    </ProgramIterationCtx.Provider>
  );
}

export function RoomIterationProvider(props: {
  room: RoomBookingItem;
  index: number;
  count: number;
  children?: React.ReactNode;
}) {
  const value = useMemo<RoomIterationContext>(
    () => ({
      room: props.room,
      index: props.index,
      ordinal: props.index + 1,
      count: props.count,
    }),
    [props.room, props.index, props.count]
  );

  return (
    <RoomIterationCtx.Provider value={value}>
      {props.children}
    </RoomIterationCtx.Provider>
  );
}

export function PosIterationProvider(props: {
  posItem: PosItemData;
  index: number;
  count: number;
  children?: React.ReactNode;
}) {
  const value = useMemo<PosIterationContext>(
    () => ({
      posItem: props.posItem,
      index: props.index,
      ordinal: props.index + 1,
      count: props.count,
    }),
    [props.posItem, props.index, props.count]
  );

  return (
    <PosIterationCtx.Provider value={value}>
      {props.children}
    </PosIterationCtx.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

export function useProgramIteration(): ProgramIterationContext | null {
  return useContext(ProgramIterationCtx);
}

export function useRoomIteration(): RoomIterationContext | null {
  return useContext(RoomIterationCtx);
}

export function usePosIteration(): PosIterationContext | null {
  return useContext(PosIterationCtx);
}

// ============================================================================
// Collections Context - Holds the full arrays for repeaters
// ============================================================================

export interface MacroCollectionsValue {
  programs: ProgramBookingItem[];
  rooms: RoomBookingItem[];
  posItems: PosItemData[];
}

const MacroCollectionsCtx = createContext<MacroCollectionsValue | null>(null);

export function MacroCollectionsProvider(props: {
  value: MacroCollectionsValue;
  children?: React.ReactNode;
}) {
  return (
    <MacroCollectionsCtx.Provider value={props.value}>
      {props.children}
    </MacroCollectionsCtx.Provider>
  );
}

export function useMacroCollections(): MacroCollectionsValue {
  const ctx = useContext(MacroCollectionsCtx);
  if (!ctx) {
    // Return defaults if no provider (useful for editor preview)
    return defaultPreviewCollections;
  }
  return ctx;
}

// ============================================================================
// Default Preview Data
// ============================================================================

export const defaultPreviewCollections: MacroCollectionsValue = {
  programs: [
    {
      Name: "Weight Loss Program",
      Days: 14,
      StartDate: "March 1, 2025",
      EndDate: "March 14, 2025",
      Price: "฿35,000",
      Quantity: 1,
    },
  ],
  rooms: [
    {
      RoomType: "Superior Room",
      Nights: 14,
      CheckInDate: "March 1, 2025",
      CheckOutDate: "March 15, 2025",
      Price: "฿21,000",
      Quantity: 1,
    },
  ],
  posItems: [
    {
      Description: "Airport Transfer",
      Quantity: 1,
      Price: "฿2,000",
    },
  ],
};

// ============================================================================
// Collection Binding Keys & Resolution
// ============================================================================

// Binding keys for collection item properties
export type CollectionBindingKey =
  // Program bindings
  | "program.Name"
  | "program.Days"
  | "program.DaysUnit" // "day" or "days" based on Days value
  | "program.StartDate"
  | "program.EndDate"
  | "program.Price"
  | "program.Quantity"
  | "program.index"
  | "program.ordinal"
  | "program.count"
  // Room bindings
  | "room.RoomType"
  | "room.Nights"
  | "room.NightsUnit" // "night" or "nights" based on Nights value
  | "room.CheckInDate"
  | "room.CheckOutDate"
  | "room.Price"
  | "room.Quantity"
  | "room.index"
  | "room.ordinal"
  | "room.count"
  // POS item bindings
  | "pos.Description"
  | "pos.Quantity"
  | "pos.Price"
  | "pos.Total" // Alias for pos.Price (for LLM convenience)
  | "pos.index"
  | "pos.ordinal"
  | "pos.count";

export const COLLECTION_BINDING_KEYS: CollectionBindingKey[] = [
  // Program
  "program.Name",
  "program.Days",
  "program.DaysUnit",
  "program.StartDate",
  "program.EndDate",
  "program.Price",
  "program.Quantity",
  "program.index",
  "program.ordinal",
  "program.count",
  // Room
  "room.RoomType",
  "room.Nights",
  "room.NightsUnit",
  "room.CheckInDate",
  "room.CheckOutDate",
  "room.Price",
  "room.Quantity",
  "room.index",
  "room.ordinal",
  "room.count",
  // POS
  "pos.Description",
  "pos.Quantity",
  "pos.Price",
  "pos.Total",
  "pos.index",
  "pos.ordinal",
  "pos.count",
];

export interface CollectionBindingOption {
  key: CollectionBindingKey;
  label: string;
  description: string;
  scope: "program" | "room" | "pos";
}

export const collectionBindingOptions: CollectionBindingOption[] = [
  // Program bindings
  { key: "program.Name", label: "Program Name", description: "Name of the program (e.g., 'Weight Loss Program')", scope: "program" },
  { key: "program.Days", label: "Program Days", description: "Number of program days", scope: "program" },
  { key: "program.DaysUnit", label: "Days Unit", description: "'day' or 'days' based on number", scope: "program" },
  { key: "program.StartDate", label: "Program Start Date", description: "Formatted start date", scope: "program" },
  { key: "program.EndDate", label: "Program End Date", description: "Formatted end date", scope: "program" },
  { key: "program.Price", label: "Program Price", description: "Formatted price WITH currency (e.g., '฿35,000') - DO NOT add ฿ before this", scope: "program" },
  { key: "program.Quantity", label: "Program Quantity", description: "Quantity (for grouped programs)", scope: "program" },
  { key: "program.index", label: "Program Index", description: "Zero-based index in the list", scope: "program" },
  { key: "program.ordinal", label: "Program Ordinal", description: "One-based position (1, 2, 3...)", scope: "program" },
  { key: "program.count", label: "Program Count", description: "Total number of programs", scope: "program" },
  // Room bindings
  { key: "room.RoomType", label: "Room Type", description: "Name of the room type (e.g., 'Superior Room')", scope: "room" },
  { key: "room.Nights", label: "Room Nights", description: "Number of nights", scope: "room" },
  { key: "room.NightsUnit", label: "Nights Unit", description: "'night' or 'nights' based on number", scope: "room" },
  { key: "room.CheckInDate", label: "Check-in Date", description: "Formatted check-in date", scope: "room" },
  { key: "room.CheckOutDate", label: "Check-out Date", description: "Formatted check-out date", scope: "room" },
  { key: "room.Price", label: "Room Price", description: "Formatted price WITH currency (e.g., '฿21,000') - DO NOT add ฿ before this", scope: "room" },
  { key: "room.Quantity", label: "Room Quantity", description: "Quantity (for grouped rooms)", scope: "room" },
  { key: "room.index", label: "Room Index", description: "Zero-based index in the list", scope: "room" },
  { key: "room.ordinal", label: "Room Ordinal", description: "One-based position (1, 2, 3...)", scope: "room" },
  { key: "room.count", label: "Room Count", description: "Total number of rooms", scope: "room" },
  // POS bindings
  { key: "pos.Description", label: "POS Description", description: "Description of the item", scope: "pos" },
  { key: "pos.Quantity", label: "POS Quantity", description: "Quantity of the item", scope: "pos" },
  { key: "pos.Price", label: "POS Price", description: "Formatted price WITH currency (e.g., '฿2,000') - DO NOT add ฿ before this", scope: "pos" },
  { key: "pos.Total", label: "POS Total", description: "Alias for pos.Price - formatted price WITH currency (e.g., '฿2,000')", scope: "pos" },
  { key: "pos.index", label: "POS Index", description: "Zero-based index in the list", scope: "pos" },
  { key: "pos.ordinal", label: "POS Ordinal", description: "One-based position (1, 2, 3...)", scope: "pos" },
  { key: "pos.count", label: "POS Count", description: "Total number of POS items", scope: "pos" },
];

/**
 * Resolve a collection binding key to its value.
 * Must be called from within the appropriate iteration provider.
 */
export function resolveCollectionBinding(
  key: CollectionBindingKey,
  programCtx: ProgramIterationContext | null,
  roomCtx: RoomIterationContext | null,
  posCtx: PosIterationContext | null
): string {
  switch (key) {
    // Program bindings
    case "program.Name":
      return programCtx?.program.Name ?? "";
    case "program.Days":
      return programCtx ? String(programCtx.program.Days) : "";
    case "program.DaysUnit":
      return programCtx ? (programCtx.program.Days === 1 ? "day" : "days") : "";
    case "program.StartDate":
      return programCtx?.program.StartDate ?? "";
    case "program.EndDate":
      return programCtx?.program.EndDate ?? "";
    case "program.Price":
      return programCtx?.program.Price ?? "";
    case "program.Quantity":
      return programCtx ? String(programCtx.program.Quantity) : "";
    case "program.index":
      return programCtx != null ? String(programCtx.index) : "";
    case "program.ordinal":
      return programCtx != null ? String(programCtx.ordinal) : "";
    case "program.count":
      return programCtx != null ? String(programCtx.count) : "";

    // Room bindings
    case "room.RoomType":
      return roomCtx?.room.RoomType ?? "";
    case "room.Nights":
      return roomCtx ? String(roomCtx.room.Nights) : "";
    case "room.NightsUnit":
      return roomCtx ? (roomCtx.room.Nights === 1 ? "night" : "nights") : "";
    case "room.CheckInDate":
      return roomCtx?.room.CheckInDate ?? "";
    case "room.CheckOutDate":
      return roomCtx?.room.CheckOutDate ?? "";
    case "room.Price":
      return roomCtx?.room.Price ?? "";
    case "room.Quantity":
      return roomCtx ? String(roomCtx.room.Quantity) : "";
    case "room.index":
      return roomCtx != null ? String(roomCtx.index) : "";
    case "room.ordinal":
      return roomCtx != null ? String(roomCtx.ordinal) : "";
    case "room.count":
      return roomCtx != null ? String(roomCtx.count) : "";

    // POS bindings
    case "pos.Description":
      return posCtx?.posItem.Description ?? "";
    case "pos.Quantity":
      return posCtx ? String(posCtx.posItem.Quantity) : "";
    case "pos.Price":
    case "pos.Total": // Alias for pos.Price
      return posCtx?.posItem.Price ?? "";
    case "pos.index":
      return posCtx != null ? String(posCtx.index) : "";
    case "pos.ordinal":
      return posCtx != null ? String(posCtx.ordinal) : "";
    case "pos.count":
      return posCtx != null ? String(posCtx.count) : "";

    default:
      return "";
  }
}

/**
 * Check if a token is a collection binding key.
 */
export function isCollectionBindingKey(token: string): token is CollectionBindingKey {
  return COLLECTION_BINDING_KEYS.includes(token as CollectionBindingKey);
}
